import React, { forwardRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import ToolBarTemplate from "../../components/experience/toolbar/ToolBarTemplate";
import "./cart.scss";
import GoBackButton from "../../components/experience/goBackButton/goBackButton";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../../context/UserContext";
import { getImage } from "../../api/ApiPath";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from "@mui/material";
import OrderService from "../../api/service/OrderService";
import { routes } from "../../__product__/navigation/routes";
import Iyilestirtoaster, { ToastTypes } from "../../components/experience/toaster/iyilestirtoaster";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { useMyNavigate } from "../../__product__/navigation/myNavigate";
const PhoneInputField = forwardRef(function PhoneInputField(props: any, ref: any) {
  return (
    <TextField
      {...props}
      name="phoneNumber"
      sx={{ height: "100%", ml: 1 }}
      inputRef={ref}
      fullWidth
      label={props.label}
      variant="outlined"
    />
  );
});

const Cart = () => {
  const { t } = useTranslation();
  const { orders, setOrders, currentOnlineMenu, currentCompanyInfo } = useUserContext();
  const [gdpr, setGdpr] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: "",
    surname: "",
    phoneNumber: "",
    address: "",
    paymentMethod: "cash",
    orderNote: ""
  });
  const [loading, setLoading] = useState(false);
  const { sendOrder } = OrderService();
  const navigate = useMyNavigate();
  const handleInputChange = (event: SelectChangeEvent | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setUserInfo({
      ...userInfo,
      [event.target.name]: event.target.value
    });
  };

  const handleOrder = () => {
    if (
      !userInfo.name ||
      !userInfo.surname ||
      !userInfo.phoneNumber ||
      !isValidPhoneNumber(userInfo.phoneNumber) ||
      !userInfo.address
    ) {
      return;
    }
    setLoading(true);
    void sendOrder({
      company: {
        id: currentCompanyInfo?.id ?? ""
      },
      orderNote: userInfo.orderNote ? userInfo.orderNote : null,
      customer: {
        phoneNumber: userInfo.phoneNumber ?? "",
        nameSurname: `${userInfo?.name} ${userInfo?.surname}`
      },
      onlineMenu: {
        id: currentOnlineMenu?.id ?? ""
      },
      deliveryAddress: userInfo?.address,
      paymentType: userInfo.paymentMethod === "cash" ? 1 : 2,
      orderedFoods: orders?.map((value) => {
        return {
          food: {
            id: value.id
          },
          orderPrice: value.orderPrice,
          quantity: value.quantity
        };
      })
    })
      .then((value) => {
        if (!value.error) {
          navigate(`${routes.verify}`, {
            state: {
              orderNo: value?.responseModel?.orderNumber
            }
          });
        } else {
          Iyilestirtoaster({
            type: ToastTypes.error,
            autoClose: false,
            message: t("order_not_complete", {
              phoneNo: currentOnlineMenu.comeAndGetPhoneNumber
            })
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleQuantityChange = (index: number, delta: number) => {
    setOrders((prevOrders) =>
      prevOrders.map((order, i) => {
        if (i === index) {
          return { ...order, quantity: Math.max(1, order.quantity + delta) };
        }
        return order;
      })
    );
  };
  const totalPrice = orders.reduce((total, order) => total + order.orderPrice * order.quantity, 0);

  const handleRemove = (index: number) => {
    setOrders((prevOrders) => prevOrders.filter((_, i) => i !== index));
  };

  return (
    <ToolBarTemplate
      backgroundColor={currentOnlineMenu.backgroundColor}
      company={currentCompanyInfo}
      titleColor={currentOnlineMenu.titleColor}
    >
      <Box className="cart-container">
        {orders.map((order, index) => (
          <Box key={index} className="order-item" sx={{ width: "100%" }}>
            <img src={getImage(order.photoUrl)} alt={order.name} />
            <Box className="order-details" sx={{ overflow: "hidden" }}>
              <h2>{order.name}</h2>
              <span className="total">
                {currentOnlineMenu.currecyTitle}
                {(order.orderPrice * order.quantity).toFixed(2)}
              </span>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Button sx={{ fontSize: "12px", minWidth: "35px" }} onClick={() => handleQuantityChange(index, -1)}>
                  -
                </Button>
                <p className="quantity">{order.quantity}</p>
                <Button sx={{ fontSize: "12px", minWidth: "35px" }} onClick={() => handleQuantityChange(index, 1)}>
                  +
                </Button>
              </Box>
              <Button
                sx={{
                  position: "absolute",
                  marginRight: "15px",
                  fontSize: "12px",
                  minWidth: "35px"
                }}
                className="remove-button"
                onClick={() => handleRemove(index)}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        ))}
        <Box className="total-price">
          {t("totalPrice")}: {currentOnlineMenu.currecyTitle} {totalPrice.toFixed(2)}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}
        >
          <TextField
            sx={{ width: "100%" }}
            required
            type="text"
            name="name"
            onChange={handleInputChange}
            label={t("name") || ""}
          />
          <TextField
            sx={{ width: "100%", marginTop: "15px" }}
            required
            type="text"
            name="surname"
            onChange={handleInputChange}
            label={t("surname") || ""}
          />
          <PhoneInput
            name="phoneNumber"
            onChange={(value: any) => {
              if (!value) {
                setPhoneError(false);
              } else {
                setPhoneError(!isValidPhoneNumber(value));
                handleInputChange({ target: { name: "phoneNumber", value } } as any);
              }
            }}
            required
            placeholder={t("phoneNumber")}
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "15px"
            }}
            error={phoneError}
            defaultCountry="TR"
            international
            initialValueFormat="national"
            countries={["TR"]}
            countryCallingCodeEditable={false}
            inputComponent={PhoneInputField}
            label={t("phoneNumber")}
            helperText={phoneError ? t("invalidPhoneNumber") : ""}
          />
          {/* <TextField
            sx={{ width: "100%", marginTop: "15px" }}
            type="text"
            required
            name="phoneNumber"
            onChange={handleInputChange}
            label={t("phoneNumber") || ""}
          /> */}
          <TextField
            sx={{ width: "100%", marginTop: "15px" }}
            type="text"
            required
            name="address"
            onChange={handleInputChange}
            label={t("address") || ""}
            multiline
            minRows={1}
            maxRows={2}
            inputProps={{ style: { overflow: "auto", whiteSpace: "normal" } }}
          />
          <TextField
            sx={{ width: "100%", marginTop: "15px" }}
            type="text"
            name="orderNote"
            onChange={handleInputChange}
            label={t("orderNote") || ""}
            multiline
            minRows={1}
            maxRows={2}
            inputProps={{ style: { overflow: "auto", whiteSpace: "normal" } }}
          />
        </Box>
        <Select
          style={{
            marginTop: "15px",
            width: "100%",
            display: "flex",
            textAlign: "center"
          }}
          name="paymentMethod"
          value={userInfo.paymentMethod}
          onChange={handleInputChange}
        >
          <MenuItem value="" disabled>
            {t("selectPaymentMethod")}
          </MenuItem>
          <MenuItem value="cash">{t("cash")}</MenuItem>
          <MenuItem value="creditCard">{t("creditCard")}</MenuItem>
        </Select>
        {totalPrice !== 0 && (
          <>
            <FormControlLabel
              style={{ alignSelf: "flex-start", marginTop: 8 }}
              control={
                <Checkbox
                  checked={gdpr}
                  onChange={(event) => {
                    setGdpr(event.target.checked);
                  }}
                />
              }
              label={t("kvkk_read")}
            />
            {loading ? (
              <LinearProgress />
            ) : (
              <Button onClick={() => handleOrder()} className="complete-order-button" disabled={!gdpr}>
                {t("completeOrder")}
              </Button>
            )}
          </>
        )}
      </Box>
      <GoBackButton />
    </ToolBarTemplate>
  );
};

export default Cart;
